import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { BookOpen, Calculator, Microscope, Globe, Languages, Palette, Music, Code, Heart, Users } from 'lucide-react';

export const Curriculum = () => {
  const curriculumAreas = [
    {
      icon: BookOpen,
      title: 'English Language Arts',
      color: 'tealAccent',
      primary: 'Amplify ELA (Grades 6-8)',
      description: 'Blended curriculum focusing on critical reading, writing, and discussion with complex texts. Built on Universal Design for Learning (UDL) principles.',
      features: [
        'High-quality diverse literary and informational texts',
        'Digital library with over 650 titles',
        'Six embedded levels of differentiation for ELLs and diverse learners',
        'Interactive Quests, Vocab App, and Solos for engaging learning',
        'Embedded assessment with robust reporting tools'
      ],
      supplemental: 'MUG Shots (Mechanics, Usage, Grammar), Accelerated Reader (AR)',
      rating: 'EdReports: All-green ratings (100% Meets Expectations across all gateways)'
    },
    {
      icon: Calculator,
      title: 'Mathematics',
      color: 'goldenOlive',
      primary: 'EdGems Math (Grades 6-8)',
      description: 'Comprehensive, customizable curriculum with inquiry-based learning and real-world connections. Uses Concrete-Representational-Abstract (CRA) instructional model.',
      features: [
        'Tiered resources by complexity with built-in accommodations',
        'Print and digital resources with interactive textbooks',
        'Research-based activities for diverse learners including special education',
        'Editable materials for teacher customization',
        'Online assignments and progress monitoring dashboards'
      ],
      supplemental: 'IXL Skill Plans, Storyboards (56-page "comic books")',
      rating: 'Adopted statewide in New Mexico (2025); Colorado DOE Reviewed'
    },
    {
      icon: Microscope,
      title: 'Science',
      color: 'sageGreen',
      primary: 'Lab-Aids Issues and Science (NGSS-Aligned)',
      description: 'Real-world problem solving through hands-on labs and investigations aligned with Next Generation Science Standards.',
      features: [
        'Each unit focuses on real-world problems (infectious disease, population growth)',
        'Hands-on labs fostering curiosity and creativity',
        'Digital portal with interactive text and editable PowerPoints',
        'Hands-On@Home program for at-home learning',
        'LABsent program with guided sheets and videos for absent students'
      ],
      supplemental: 'Curriculum Builder tool for customization',
      rating: 'EdReports: All-green ratings (84-91% Meets Expectations)'
    },
    {
      icon: Globe,
      title: 'Social Studies',
      color: 'coralRed',
      primary: 'National Geographic Social Studies',
      description: 'Engaging stories, powerful visuals, and project-based learning with cross-curricular connections.',
      features: [
        'Story Maps: Interactive combinations of maps, graphs, and images',
        'Stories from National Geographic Explorers and photographers',
        'Primary sources with audio options for accessibility',
        'Modified text and audio for striving readers',
        '24/7 online access via MindTap, Google Classroom, and LMS integration'
      ],
      supplemental: 'Project-based learning connecting themes to students\' lives',
      rating: 'Cross-curricular projects (math, reading, science)'
    },
    {
      icon: Languages,
      title: 'English Language Learners',
      color: 'navyBlue',
      primary: 'Kid-Inspired Classroom (K-12)',
      description: 'Comprehensive language program with step-by-step curriculum and engaging, student-centered activities.',
      features: [
        'Wide variety of teacher-tested resources',
        'Research-based ELL teaching strategies',
        'Complete unit plans with printable materials',
        'Differentiated lesson content',
        'Content-based instruction integrating academic skills'
      ],
      supplemental: 'Integrated with Amplify ELA for additional support',
      rating: 'Subscription-based with Standard and Pro tiers'
    },
    {
      icon: Heart,
      title: 'Physical Education',
      color: 'sageGreen',
      primary: 'HealthSmart K-12',
      description: 'Skills-based health education empowering students to adopt and maintain healthy behaviors.',
      features: [
        'Over 400 lessons across K-12 with flexibility',
        'Science-informed approaches to impact health behaviors',
        'Available in English and Spanish, ADA compliant',
        'LMS compatible with standards alignment',
        'Clear teaching steps focused on essential concepts'
      ],
      supplemental: 'Customizable lessons aligned to state standards',
      rating: 'Evidence-based health education curriculum'
    },
    {
      icon: Palette,
      title: 'Visual Arts',
      color: 'tealAccent',
      primary: 'FLEX Curriculum by The Art of Education',
      description: 'Standards-aligned K-12 visual arts curriculum with ready-to-teach materials.',
      features: [
        'Pre-built K-12 visual arts units ready to customize',
        'Comprehensive lesson plans with objectives and material lists',
        'Artist bios connecting students with diverse artists',
        'Ad-free videos for scaffolding learning',
        'Formative, summative assessments, reflections, and critiques'
      ],
      supplemental: 'Premium worksheets, reference materials, anchor charts',
      rating: 'Vertical alignment supported across grades K-12'
    },
    {
      icon: Music,
      title: 'Performing Arts',
      color: 'goldenOlive',
      primary: 'Theatre, Band, and Choir Programs',
      description: 'Comprehensive performing arts education fostering creativity and expression.',
      features: [
        'Theatre program for dramatic arts and performance',
        'Band instruction for instrumental music',
        'Choir for vocal music education',
        'Performance opportunities and showcases'
      ],
      supplemental: 'Integrated with school-wide events and community performances',
      rating: 'Specialized performance spaces on 38-acre campus'
    },
    {
      icon: Code,
      title: 'Coding & Robotics',
      color: 'sageGreen',
      primary: 'Code.org & How to Train Your Robot',
      description: 'Computer science exploration and machine learning through hands-on activities.',
      features: [
        'Computer Science Discoveries (Grades 6-10): Building websites, apps, animations',
        'Artificial Intelligence (AI) curriculum with real-world integrations',
        'How to Train Your Robot: 30-hour machine learning and ethics curriculum',
        'Game Design and Maker Curricula fostering critical thinking',
        'Hour of Code activities and professional learning resources'
      ],
      supplemental: 'Free curriculum from MIT Media Lab and Code.org',
      rating: 'Creative Commons licensed with educator guides'
    },
    {
      icon: Users,
      title: 'Family & Consumer Sciences',
      color: 'coralRed',
      primary: 'Exploring Family & Consumer Sciences (ICEE)',
      description: 'Developing key life skills across food, childcare, design, and relationships.',
      features: [
        'Food and nutrition with nutritional labeling',
        'Childcare and safety practices',
        'Interior and fashion design',
        'Consumer services and decision making',
        'Family relationships and conflict management'
      ],
      supplemental: 'Science in food handling and storage',
      rating: 'Comprehensive life skills development'
    }
  ];

  return (
    <section id="curriculum" className="py-20 px-4 sm:px-6 lg:px-8 bg-navyBlue/5">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <div className="inline-block bg-goldenOlive/10 px-6 py-2 rounded-full mb-4">
            <span className="text-goldenOlive font-semibold">Academic Excellence</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">Comprehensive, Research-Based Curriculum</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Our educator-designed curriculum combines proven programs with innovative approaches, ensuring every student receives a <span className="font-semibold text-goldenOlive">personalized, engaging education</span>.
          </p>
        </div>

        {/* Classroom Learning Image */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-16 max-h-[400px]">
          <img 
            src={process.env.REACT_APP_CLASSROOM_IMAGE_URL} 
            alt="Doctrina students engaged in collaborative learning" 
            className="w-full h-full object-cover" style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navyBlue/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">Engaged Learning, Every Day</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Our students thrive in collaborative classrooms where research-based curricula meets innovative teaching methods.
            </p>
          </div>
        </div>

        {/* Core Pillars */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
            <div className="text-3xl font-bold text-tealAccent mb-2">PBL</div>
            <p className="text-sm text-gray-600">Project-Based Learning</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
            <div className="text-3xl font-bold text-goldenOlive mb-2">SEL</div>
            <p className="text-sm text-gray-600">Social-Emotional Learning</p>
            <p className="text-xs text-gray-500 mt-1">6 Minute SEL Curriculum</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
            <div className="text-3xl font-bold text-sageGreen mb-2">1:1</div>
            <p className="text-sm text-gray-600">Technology Integration</p>
            <p className="text-xs text-gray-500 mt-1">Chromebooks for every student</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
            <div className="text-3xl font-bold text-coralRed mb-2">NGSS</div>
            <p className="text-sm text-gray-600">Standards-Aligned</p>
            <p className="text-xs text-gray-500 mt-1">All subjects meet state standards</p>
          </div>
        </div>

        {/* Accordion for Curriculum Details */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <Accordion type="single" collapsible className="w-full">
            {curriculumAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-200 last:border-0">
                  <AccordionTrigger className="hover:no-underline py-6 group">
                    <div className="flex items-center space-x-4">
                      <div className={`bg-${area.color}/10 w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className={`text-${area.color}`} size={24} />
                      </div>
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-navyBlue">{area.title}</h3>
                        <p className="text-sm text-gray-600">{area.primary}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="ml-16 space-y-4">
                      <p className="text-gray-700 leading-relaxed">{area.description}</p>
                      <div>
                        <h4 className="font-semibold text-navyBlue mb-2">Key Features:</h4>
                        <ul className="space-y-2">
                          {area.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className={`text-${area.color} mt-1`}>•</span>
                              <span className="text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600"><span className="font-semibold">Supplemental:</span> {area.supplemental}</p>
                      </div>
                      {area.rating && (
                        <div className="bg-tealAccent/10 rounded-lg p-4 border-l-4 border-tealAccent">
                          <p className="text-sm text-gray-700"><span className="font-semibold">Rating/Recognition:</span> {area.rating}</p>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* Special Programs */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-navyBlue to-navyBlue/90 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Special Education Excellence</h3>
            <p className="text-white/90 mb-4">Comprehensive support with research-based HMH programs:</p>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-tealAccent">✓</span>
                <span>Math 180 & Read 180 intervention programs</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-tealAccent">✓</span>
                <span>Waggle adaptive learning technology</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-tealAccent">✓</span>
                <span>IEP progress monitoring with built-in analytics</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-tealAccent">✓</span>
                <span>Customized learning paths and explicit instruction</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-tealAccent">✓</span>
                <span>NWEA MAP Growth integration for placement</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-tealAccent to-sageGreen rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Restorative Practices</h3>
            <p className="text-white/90 mb-4">Discipline Without Stress teaching model:</p>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-navyBlue">✓</span>
                <span><strong>Positivity:</strong> Communicate what you want, not what you don't want</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-navyBlue">✓</span>
                <span><strong>Choice:</strong> Teach Choice-Response Thinking and Impulse Management</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-navyBlue">✓</span>
                <span><strong>Reflection:</strong> Prompt change through reflective questions</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-navyBlue">✓</span>
                <span>Teacher-led Discipline Committee</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-navyBlue">✓</span>
                <span>Six-Minute SEL daily lessons (five core competencies)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Curriculum;